import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'react-toastify';
import '@/styles/PropertyPagesModern.scss';
import * as Sentry from '@sentry/react';

/**
 * PropertyAccessTab Component
 * 
 * Manages property access permissions for users and roles with modern UI/UX.
 * Features:
 * - Dual-tab interface for roles and users
 * - Real-time search filtering
 * - Visual selection indicators
 * - Enhanced accessibility
 * - Loading states and error handling
 */
const PropertyAccessTab = () => {
    const { apiFetch, user } = useAuth();
    
    // State for roles and users
    const [roles, setRoles] = useState([]);
    const [users, setUsers] = useState([]);
    const [selectedRole, setSelectedRole] = useState(null);
    const [selectedUser, setSelectedUser] = useState(null);
    
    // State for properties and property groups
    const [properties, setProperties] = useState([]);
    const [propertyGroups, setPropertyGroups] = useState([]);
    
    // State for current selections
    const [rolePropertyRestrictions, setRolePropertyRestrictions] = useState([]);
    const [userPropertyRestrictions, setUserPropertyRestrictions] = useState([]);
    const [rolePropertyGroups, setRolePropertyGroups] = useState([]);
    const [userPropertyGroups, setUserPropertyGroups] = useState([]);
    
    // UI state
    const [activeTab, setActiveTab] = useState('roles'); // 'roles' or 'users'
    const [searchTerm, setSearchTerm] = useState('');
    const [searchRoleUser, setSearchRoleUser] = useState('');
    const [isInitialLoading, setIsInitialLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);

    // ── DATA FETCHING FUNCTIONS ───────────────────────────────────────────────
    // Declared before useEffect hooks to avoid "accessed before declaration" lint errors.
    // Wrapped in useCallback to provide stable references for effect dependencies.

    /**
     * Fetches all roles.
     */
    const fetchRoles = useCallback(async () => {
        try {
            const response = await apiFetch('/api/Permissions/roles');
            const data = await response.json();
            if (data.success) {
                setRoles(data.data || []);
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error fetching roles:', error);
            toast.error('Failed to load roles');
        }
    }, [apiFetch]);

    /**
     * Fetches all active users.
     */
    const fetchUsers = useCallback(async () => {
        try {
            const response = await apiFetch('/api/Permissions/active-users');
            const data = await response.json();
            if (data.success) {
                setUsers(data.data || []);
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error fetching users:', error);
            toast.error('Failed to load users');
        }
    }, [apiFetch]);

    /**
     * Fetches all properties.
     */
    const fetchProperties = useCallback(async () => {
        try {
            const response = await apiFetch('/api/Property/all');
            const data = await response.json();
            if (data.success) {
                setProperties(data.data || []);
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error fetching properties:', error);
            toast.error('Failed to load properties');
        }
    }, [apiFetch]);

    /**
     * Fetches all property groups.
     */
    const fetchPropertyGroups = useCallback(async () => {
        try {
            const response = await apiFetch('/api/Property/groups/all');
            const data = await response.json();
            if (data.success) {
                setPropertyGroups(data.data || []);
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error fetching property groups:', error);
            toast.error('Failed to load property groups');
        }
    }, [apiFetch]);

    /**
     * Fetches property restrictions for a role.
     */
    const fetchRolePropertyRestrictions = useCallback(async (roleID) => {
        try {
            const response = await apiFetch(`/api/Permissions/role-property-restrictions/${roleID}`);
            const data = await response.json();
            if (data.success) {
                setRolePropertyRestrictions(data.data || []);
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error fetching role property restrictions:', error);
        }
    }, [apiFetch]);

    /**
     * Fetches property restrictions for a user.
     */
    const fetchUserPropertyRestrictions = useCallback(async (userID) => {
        try {
            const response = await apiFetch(`/api/Permissions/user-property-restrictions/${userID}`);
            const data = await response.json();
            if (data.success) {
                setUserPropertyRestrictions(data.data || []);
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error fetching user property restrictions:', error);
        }
    }, [apiFetch]);

    /**
     * Fetches property group assignments for a role.
     */
    const fetchRolePropertyGroups = useCallback(async (roleID) => {
        try {
            const response = await apiFetch(`/api/Permissions/role-property-groups/${roleID}`);
            const data = await response.json();
            if (data.success) {
                setRolePropertyGroups(data.data || []);
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error fetching role property groups:', error);
        }
    }, [apiFetch]);

    /**
     * Fetches property group assignments for a user.
     */
    const fetchUserPropertyGroups = useCallback(async (userID) => {
        try {
            const response = await apiFetch(`/api/Permissions/user-property-groups/${userID}`);
            const data = await response.json();
            if (data.success) {
                setUserPropertyGroups(data.data || []);
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error fetching user property groups:', error);
        }
    }, [apiFetch]);

    // ── EFFECTS ─────────────────────────────────────────────────────────────────

    useEffect(() => {
        const loadData = async () => {
            setIsInitialLoading(true);
            await Promise.all([
                fetchRoles(),
                fetchUsers(),
                fetchProperties(),
                fetchPropertyGroups()
            ]);
            setIsInitialLoading(false);
        };
        loadData();
    }, [fetchRoles, fetchUsers, fetchProperties, fetchPropertyGroups]);

    useEffect(() => {
        if (selectedRole) {
            const loadRoleData = async () => {
                await Promise.all([
                    fetchRolePropertyRestrictions(selectedRole.roleID),
                    fetchRolePropertyGroups(selectedRole.roleID)
                ]);
            };
            loadRoleData();
        }
    }, [selectedRole, fetchRolePropertyRestrictions, fetchRolePropertyGroups]);

    useEffect(() => {
        if (selectedUser) {
            const loadUserData = async () => {
                await Promise.all([
                    fetchUserPropertyRestrictions(selectedUser.userID),
                    fetchUserPropertyGroups(selectedUser.userID)
                ]);
            };
            loadUserData();
        }
    }, [selectedUser, fetchUserPropertyRestrictions, fetchUserPropertyGroups]);

    /**
     * Saves role property restrictions AND property group assignments together.
     * Both individual property selections and property group selections are persisted
     * in parallel to ensure consistent state.
     */
    const handleSaveRolePropertyRestrictions = async () => {
        if (!selectedRole) return;

        setIsSaving(true);
        const selectedPropertyIDs = rolePropertyRestrictions.map(r => r.propertyID);
        const responsiblePropertyIDs = rolePropertyRestrictions
            .filter(r => r.isResponsible)
            .map(r => r.propertyID);
        const selectedPropertyGroupIDs = rolePropertyGroups.map(g => g.propertyGroupID);

        // Use allSettled so that a thrown error (network failure, 401, abort) from one
        // fetch does not short-circuit the other and prevents the partial-success branch
        // from being unreachable when only one of the two endpoints fails.
        const [restrictionsResult, groupsResult] = await Promise.allSettled([
            apiFetch('/api/Permissions/save-role-property-restrictions', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    roleID: selectedRole.roleID,
                    propertyIDs: selectedPropertyIDs,
                    responsiblePropertyIDs,
                    modifiedByUserID: user?.UserID || 0
                })
            }).then(res => res.json()),
            apiFetch('/api/Permissions/save-role-property-groups', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    roleID: selectedRole.roleID,
                    propertyGroupIDs: selectedPropertyGroupIDs,
                    modifiedByUserID: user?.UserID || 0
                })
            }).then(res => res.json())
        ]);

        // Determine per-endpoint outcome: fulfilled with success:true counts as success
        const restrictionsOk = restrictionsResult.status === 'fulfilled' && restrictionsResult.value?.success === true;
        const groupsOk = groupsResult.status === 'fulfilled' && groupsResult.value?.success === true;

        /**
         * Extracts the most informative error message from an allSettled result entry.
         * Prefers the API response body message, falls back to the thrown error message.
         * @param {PromiseSettledResult} result - A single allSettled result
         * @param {string} fallback - Default message if neither source has text
         */
        const getResultError = (result, fallback) =>
            result.value?.message || result.reason?.message || fallback;

        if (restrictionsOk && groupsOk) {
            toast.success('Role property access saved successfully');
        } else if (restrictionsOk || groupsOk) {
            // Partial success - gather the failure reason from the failing endpoint
            const failedMessage = !restrictionsOk
                ? getResultError(restrictionsResult, 'Failed to save role property restrictions')
                : getResultError(groupsResult, 'Failed to save role property groups');
            toast.warning(`Partial save: ${failedMessage}`);
        } else {
            // Both failed - combine error messages from whichever detail is available
            const errorParts = [
                getResultError(restrictionsResult, 'Failed to save property restrictions'),
                getResultError(groupsResult, 'Failed to save property groups')
            ].filter(Boolean);
            toast.error(errorParts.join('; '));
            console.error('Error saving role property access:', restrictionsResult.reason, groupsResult.reason);
        }

        setIsSaving(false);
    };

    /**
     * Saves user property restrictions AND property group assignments together.
     * Both individual property selections and property group selections are persisted
     * in parallel to ensure consistent state.
     */
    const handleSaveUserPropertyRestrictions = async () => {
        if (!selectedUser) return;

        setIsSaving(true);
        const selectedPropertyIDs = userPropertyRestrictions.map(r => r.propertyID);
        const responsiblePropertyIDs = userPropertyRestrictions
            .filter(r => r.isResponsible)
            .map(r => r.propertyID);
        const selectedPropertyGroupIDs = userPropertyGroups.map(g => g.propertyGroupID);

        // Use allSettled so that a thrown error (network failure, 401, abort) from one
        // fetch does not short-circuit the other and prevents the partial-success branch
        // from being unreachable when only one of the two endpoints fails.
        const [restrictionsResult, groupsResult] = await Promise.allSettled([
            apiFetch('/api/Permissions/save-user-property-restrictions', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    userID: selectedUser.userID,
                    propertyIDs: selectedPropertyIDs,
                    responsiblePropertyIDs,
                    modifiedByUserID: user?.UserID || 0
                })
            }).then(res => res.json()),
            apiFetch('/api/Permissions/save-user-property-groups', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    userID: selectedUser.userID,
                    propertyGroupIDs: selectedPropertyGroupIDs,
                    modifiedByUserID: user?.UserID || 0
                })
            }).then(res => res.json())
        ]);

        // Determine per-endpoint outcome: fulfilled with success:true counts as success
        const restrictionsOk = restrictionsResult.status === 'fulfilled' && restrictionsResult.value?.success === true;
        const groupsOk = groupsResult.status === 'fulfilled' && groupsResult.value?.success === true;

        /**
         * Extracts the most informative error message from an allSettled result entry.
         * Prefers the API response body message, falls back to the thrown error message.
         * @param {PromiseSettledResult} result - A single allSettled result
         * @param {string} fallback - Default message if neither source has text
         */
        const getResultError = (result, fallback) =>
            result.value?.message || result.reason?.message || fallback;

        if (restrictionsOk && groupsOk) {
            toast.success('User property access saved successfully');
        } else if (restrictionsOk || groupsOk) {
            // Partial success - gather the failure reason from the failing endpoint
            const failedMessage = !restrictionsOk
                ? getResultError(restrictionsResult, 'Failed to save user property restrictions')
                : getResultError(groupsResult, 'Failed to save user property groups');
            toast.warning(`Partial save: ${failedMessage}`);
        } else {
            // Both failed - combine error messages from whichever detail is available
            const errorParts = [
                getResultError(restrictionsResult, 'Failed to save property restrictions'),
                getResultError(groupsResult, 'Failed to save property groups')
            ].filter(Boolean);
            toast.error(errorParts.join('; '));
            console.error('Error saving user property access:', restrictionsResult.reason, groupsResult.reason);
        }

        setIsSaving(false);
    };

    /**
     * Toggles a property in the current selection.
     */
    const toggleProperty = (property, isRole) => {
        if (isRole) {
            const exists = rolePropertyRestrictions.some(r => r.propertyID === property.propertyID);
            if (exists) {
                setRolePropertyRestrictions(rolePropertyRestrictions.filter(r => r.propertyID !== property.propertyID));
            } else {
                setRolePropertyRestrictions([...rolePropertyRestrictions, { propertyID: property.propertyID }]);
            }
        } else {
            const exists = userPropertyRestrictions.some(r => r.propertyID === property.propertyID);
            if (exists) {
                setUserPropertyRestrictions(userPropertyRestrictions.filter(r => r.propertyID !== property.propertyID));
            } else {
                setUserPropertyRestrictions([...userPropertyRestrictions, { propertyID: property.propertyID }]);
            }
        }
    };

    /**
     * Toggles a property group in the current selection.
     */
    const togglePropertyGroup = (group, isRole) => {
        if (isRole) {
            const exists = rolePropertyGroups.some(g => g.propertyGroupID === group.propertyGroupID);
            if (exists) {
                setRolePropertyGroups(rolePropertyGroups.filter(g => g.propertyGroupID !== group.propertyGroupID));
            } else {
                setRolePropertyGroups([...rolePropertyGroups, { propertyGroupID: group.propertyGroupID }]);
            }
        } else {
            const exists = userPropertyGroups.some(g => g.propertyGroupID === group.propertyGroupID);
            if (exists) {
                setUserPropertyGroups(userPropertyGroups.filter(g => g.propertyGroupID !== group.propertyGroupID));
            } else {
                setUserPropertyGroups([...userPropertyGroups, { propertyGroupID: group.propertyGroupID }]);
            }
        }
    };

    /**
     * Toggles the "Responsible" flag for a property in the current selection.
     * Only applicable to properties that are already selected (checked).
     */
    const toggleResponsible = (propertyID, isRole) => {
        if (isRole) {
            setRolePropertyRestrictions(prev =>
                prev.map(r => r.propertyID === propertyID
                    ? { ...r, isResponsible: !r.isResponsible }
                    : r
                )
            );
        } else {
            setUserPropertyRestrictions(prev =>
                prev.map(r => r.propertyID === propertyID
                    ? { ...r, isResponsible: !r.isResponsible }
                    : r
                )
            );
        }
    };

    const filteredProperties = properties.filter(p =>
        (p.propertyName && p.propertyName.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (p.comparativeName && p.comparativeName.toLowerCase().includes(searchTerm.toLowerCase()))
    ).sort((a, b) => a.propertyName.localeCompare(b.propertyName));

    const filteredRoles = roles.filter(r =>
        r.roleName.toLowerCase().includes(searchRoleUser.toLowerCase())
    );

    const filteredUsers = users.filter(u =>
        `${u.firstName} ${u.lastName}`.toLowerCase().includes(searchRoleUser.toLowerCase())
    );

    const getSelectionCount = () => {
        if (activeTab === 'roles') {
            return rolePropertyRestrictions.length + rolePropertyGroups.length;
        } else {
            return userPropertyRestrictions.length + userPropertyGroups.length;
        }
    };

    return (
        <div className="property-access-container-modern">
            {/* Header */}
            <div className="properties-header">
                <h1>🏢 Property Access Permissions</h1>
                <p style={{ margin: '8px 0 0 0', color: '#718096', fontSize: '0.95rem' }}>
                    Configure which properties users and roles can access. Leave empty to grant access to all properties.
                </p>
            </div>

            {isInitialLoading ? (
                <div className="loading-state">
                    <div className="spinner" aria-label="Loading data"></div>
                    <p>Loading property access permissions...</p>
                </div>
            ) : (
                <>
                    {/* Role/User Tabs */}
                    <div className="tab-buttons-modern">
                        <button
                            className={`tab-button ${activeTab === 'roles' ? 'active' : ''}`}
                            onClick={() => {
                                setActiveTab('roles');
                                setSelectedUser(null);
                                setSearchRoleUser('');
                            }}
                            aria-label="Roles tab"
                        >
                            👥 Roles
                        </button>
                        <button
                            className={`tab-button ${activeTab === 'users' ? 'active' : ''}`}
                            onClick={() => {
                                setActiveTab('users');
                                setSelectedRole(null);
                                setSearchRoleUser('');
                            }}
                            aria-label="Users tab"
                        >
                            👤 Users
                        </button>
                    </div>

                    <div className="permissions-content-grid-modern">
                        {/* Left Panel: Roles/Users List */}
                        <div className="selection-panel-modern">
                            <div className="panel-header-modern">
                                <h3>{activeTab === 'roles' ? 'Select Role' : 'Select User'}</h3>
                                <span className="selection-badge">
                                    {activeTab === 'roles' ? filteredRoles.length : filteredUsers.length} {activeTab === 'roles' ? 'roles' : 'users'}
                                </span>
                            </div>

                            {/* Search for roles/users */}
                            <div className="search-container">
                                <span className="search-icon">🔍</span>
                                <input
                                    type="text"
                                    className="search-input"
                                    placeholder={`Search ${activeTab === 'roles' ? 'roles' : 'users'}...`}
                                    value={searchRoleUser}
                                    onChange={(e) => setSearchRoleUser(e.target.value)}
                                    aria-label={`Search ${activeTab === 'roles' ? 'roles' : 'users'}`}
                                />
                            </div>

                            {activeTab === 'roles' ? (
                                <ul className="selection-list-modern">
                                    {filteredRoles.length === 0 ? (
                                        <li className="empty-list-item">No roles found</li>
                                    ) : (
                                        filteredRoles.map((role) => (
                                            <li
                                                key={role.roleID}
                                                className={`selection-item ${selectedRole?.roleID === role.roleID ? 'selected' : ''}`}
                                                onClick={() => setSelectedRole(role)}
                                            >
                                                <span className="item-icon">👥</span>
                                                <span className="item-name">{role.roleName}</span>
                                                {selectedRole?.roleID === role.roleID && (
                                                    <span className="selected-indicator">✓</span>
                                                )}
                                            </li>
                                        ))
                                    )}
                                </ul>
                            ) : (
                                <ul className="selection-list-modern">
                                    {filteredUsers.length === 0 ? (
                                        <li className="empty-list-item">No users found</li>
                                    ) : (
                                        filteredUsers.map((user) => (
                                            <li
                                                key={user.userID}
                                                className={`selection-item ${selectedUser?.userID === user.userID ? 'selected' : ''}`}
                                                onClick={() => setSelectedUser(user)}
                                            >
                                                <span className="item-icon">👤</span>
                                                <span className="item-name">{user.firstName} {user.lastName}</span>
                                                {selectedUser?.userID === user.userID && (
                                                    <span className="selected-indicator">✓</span>
                                                )}
                                            </li>
                                        ))
                                    )}
                                </ul>
                            )}
                        </div>

                        {/* Right Panel: Property Selection */}
                        <div className="properties-panel-modern">
                            {(activeTab === 'roles' && selectedRole) || (activeTab === 'users' && selectedUser) ? (
                                <>
                                    <div className="panel-header-modern">
                                        <h3>
                                            {activeTab === 'roles'
                                                ? `Property Access for ${selectedRole.roleName}`
                                                : `Property Access for ${selectedUser.firstName} ${selectedUser.lastName}`}
                                        </h3>
                                        <span className="selection-count">
                                            {getSelectionCount()} selected
                                        </span>
                                    </div>

                                    <div className="help-text-box">
                                        <span className="help-icon">ℹ️</span>
                                        <p>
                                            Select properties this {activeTab === 'roles' ? 'role' : 'user'} can access.
                                            Leave empty to grant access to all properties.
                                        </p>
                                    </div>

                                    {/* Property Search */}
                                    <div className="search-container">
                                        <span className="search-icon">🔍</span>
                                        <input
                                            type="text"
                                            className="search-input"
                                            placeholder="Search properties..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            aria-label="Search properties"
                                        />
                                    </div>

                                    {/* Property Groups Section */}
                                    <div className="section-modern">
                                        <h4>🏬 Property Groups ({propertyGroups.filter(g => 
                                            activeTab === 'roles'
                                                ? rolePropertyGroups.some(rg => rg.propertyGroupID === g.propertyGroupID)
                                                : userPropertyGroups.some(ug => ug.propertyGroupID === g.propertyGroupID)
                                        ).length} selected)</h4>
                                        {propertyGroups.length === 0 ? (
                                            <div className="empty-list">
                                                <p>No property groups available</p>
                                            </div>
                                        ) : (
                                            <div className="checkbox-list-modern checkbox-list-short">
                                                {propertyGroups.map((group) => {
                                                    const isSelected = activeTab === 'roles'
                                                        ? rolePropertyGroups.some(g => g.propertyGroupID === group.propertyGroupID)
                                                        : userPropertyGroups.some(g => g.propertyGroupID === group.propertyGroupID);
                                                    return (
                                                        <label key={group.propertyGroupID} className="checkbox-item-modern">
                                                            <input
                                                                type="checkbox"
                                                                checked={isSelected}
                                                                onChange={() => togglePropertyGroup(group, activeTab === 'roles')}
                                                                aria-label={`Select group ${group.groupName}`}
                                                            />
                                                            <span className="checkbox-label">
                                                                {group.groupName} 
                                                                <span className="item-count">({group.propertyCount} properties)</span>
                                                            </span>
                                                        </label>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>

                                    {/* Individual Properties Section */}
                                    <div className="section-modern section-with-spacing">
                                        <h4>🏢 Individual Properties ({filteredProperties.filter(p => 
                                            activeTab === 'roles'
                                                ? rolePropertyRestrictions.some(r => r.propertyID === p.propertyID)
                                                : userPropertyRestrictions.some(r => r.propertyID === p.propertyID)
                                        ).length} selected)</h4>
                                        <div className="checkbox-list-modern">
                                            {filteredProperties.length === 0 ? (
                                                <div className="empty-list">
                                                    <p>No properties found</p>
                                                </div>
                                            ) : (
                                                filteredProperties.map((property) => {
                                                    const isSelected = activeTab === 'roles'
                                                        ? rolePropertyRestrictions.some(r => r.propertyID === property.propertyID)
                                                        : userPropertyRestrictions.some(r => r.propertyID === property.propertyID);
                                                    const restriction = activeTab === 'roles'
                                                        ? rolePropertyRestrictions.find(r => r.propertyID === property.propertyID)
                                                        : userPropertyRestrictions.find(r => r.propertyID === property.propertyID);
                                                    return (
                                                        <label key={property.propertyID} className="checkbox-item-modern">
                                                            <input
                                                                type="checkbox"
                                                                checked={isSelected}
                                                                onChange={() => toggleProperty(property, activeTab === 'roles')}
                                                                aria-label={`Select property ${property.propertyName}`}
                                                            />
                                                            <span className="checkbox-label">
                                                                {property.propertyName}
                                                                {property.comparativeName && (
                                                                    <span className="item-detail"> ({property.comparativeName})</span>
                                                                )}
                                                            </span>
                                                            {isSelected && (
                                                                <span
                                                                    className="responsible-toggle"
                                                                    onClick={(e) => e.stopPropagation()}
                                                                    title="Mark this role/user as responsible for this property"
                                                                >
                                                                    <input
                                                                        type="checkbox"
                                                                        id={`responsible-${property.propertyID}-${activeTab}`}
                                                                        checked={!!restriction?.isResponsible}
                                                                        onChange={() => toggleResponsible(property.propertyID, activeTab === 'roles')}
                                                                        aria-label={`Responsible for ${property.propertyName}`}
                                                                    />
                                                                    <label
                                                                        htmlFor={`responsible-${property.propertyID}-${activeTab}`}
                                                                        className="responsible-label"
                                                                    >
                                                                        Responsible
                                                                    </label>
                                                                </span>
                                                            )}
                                                        </label>
                                                    );
                                                })
                                            )}
                                        </div>
                                    </div>

                                    {/* Save Button */}
                                    <div className="save-button-container-modern">
                                        <button
                                            onClick={activeTab === 'roles' ? handleSaveRolePropertyRestrictions : handleSaveUserPropertyRestrictions}
                                            className="btn-primary btn-large-action"
                                            disabled={isSaving}
                                        >
                                            {isSaving ? '💾 Saving...' : '💾 Save Property Access'}
                                        </button>
                                    </div>
                                </>
                            ) : (
                                <div className="no-selection-modern">
                                    <div className="no-selection-icon">
                                        {activeTab === 'roles' ? '👥' : '👤'}
                                    </div>
                                    <h3>Select a {activeTab === 'roles' ? 'Role' : 'User'}</h3>
                                    <p>Choose a {activeTab === 'roles' ? 'role' : 'user'} from the list to configure property access permissions</p>
                                </div>
                            )}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default PropertyAccessTab;
